import {
  isTenantAssetContentType,
  MAX_TENANT_ASSET_BYTES,
  type TenantAssetContentType,
  type TenantAssetKind,
} from "@loot-os/api-contract";

export const TENANT_ASSET_FIELDS = {
  logo: "logoUrl",
  favicon: "faviconUrl",
  badge: "badgeUrl",
} as const satisfies Record<TenantAssetKind, "logoUrl" | "faviconUrl" | "badgeUrl">;

export type TenantAssetUrlField = (typeof TENANT_ASSET_FIELDS)[TenantAssetKind];

/** What the server's upload start answers: the key to finish with, and where to PUT the file. */
export interface TenantAssetUploadTicket {
  uploadKey: string;
  url: string;
  headers: Record<string, string>;
}

/** A picked file's type and size, which the upload URL is signed for. */
export interface TenantAssetUploadStart {
  contentType: TenantAssetContentType;
  byteLength: number;
}

/**
 * Uploads one picked file: `start` signs a PUT for its type and size, the browser sends the bytes
 * straight to the bucket (never through the dashboard API, whose Lambda caps a request at 6 MB),
 * and `finish` has the server check what landed. SVG and oversized files are refused here so the
 * picker fails immediately; the server checks both again. `kind` swaps the image check for
 * another type (sounds pass MP3 alone); it is the same flow and size cap.
 */
export function uploadTenantAssetFile<T>(
  file: File,
  start: (input: TenantAssetUploadStart) => Promise<TenantAssetUploadTicket>,
  finish: (uploadKey: string) => Promise<T>,
): Promise<T>;
export function uploadTenantAssetFile<T, C extends string>(
  file: File,
  start: (input: { contentType: C; byteLength: number }) => Promise<TenantAssetUploadTicket>,
  finish: (uploadKey: string) => Promise<T>,
  kind: { accept: (type: string) => type is C; acceptLabel: string },
): Promise<T>;
export async function uploadTenantAssetFile<T>(
  file: File,
  start: (input: { contentType: never; byteLength: number }) => Promise<TenantAssetUploadTicket>,
  finish: (uploadKey: string) => Promise<T>,
  kind?: { accept: (type: string) => boolean; acceptLabel: string },
): Promise<T> {
  const contentType = kind
    ? requireContentType(file, kind.accept, kind.acceptLabel)
    : requireTenantAssetContentType(file);
  if (file.size > MAX_TENANT_ASSET_BYTES) {
    throw new Error(
      `the file is larger than ${Math.floor(MAX_TENANT_ASSET_BYTES / 1024 / 1024)} MB`,
    );
  }
  // The overloads above guarantee `contentType` is the type `start` was written for.
  const ticket = await start({ contentType: contentType as never, byteLength: file.size });
  // A `File` body would otherwise be sent as `file.type`, which for a normalized type is not the
  // type the URL was signed for.
  const headers = { ...ticket.headers };
  if (kind && !Object.keys(headers).some((name) => name.toLowerCase() === "content-type")) {
    headers["Content-Type"] = contentType;
  }
  const put = await fetch(ticket.url, { method: "PUT", headers, body: file });
  if (!put.ok) throw new Error(`the upload was refused (${put.status})`);
  return finish(ticket.uploadKey);
}

const CONTENT_TYPE_BY_EXTENSION: Record<string, TenantAssetContentType> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  avif: "image/avif",
};

/** The file's type as the browser reports it, else from its extension (some report AVIF as ""). */
function requireTenantAssetContentType(file: File): TenantAssetContentType {
  if (isTenantAssetContentType(file.type)) return file.type;
  const extension = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  const fromName = extension ? CONTENT_TYPE_BY_EXTENSION[extension] : undefined;
  if (file.type === "" && fromName) return fromName;
  throw new Error("only PNG, JPEG, WEBP and AVIF images are allowed");
}

/**
 * The file's reported type when `accept` allows it. A file named `.mp3` that the browser reports
 * as "" or `audio/mp3` (Windows does) is read as `audio/mpeg`, the one type sent on. `acceptLabel`
 * is the refusal's wording.
 */
function requireContentType(
  file: File,
  accept: (type: string) => boolean,
  acceptLabel: string,
): string {
  if (accept(file.type)) return file.type;
  const mp3Name = /\.mp3$/i.test(file.name);
  if (mp3Name && (file.type === "" || file.type === "audio/mp3") && accept("audio/mpeg")) {
    return "audio/mpeg";
  }
  throw new Error(acceptLabel);
}
